import {range} from '@yandex-delivery/utils'
import dayjs from 'dayjs'

/**
 * Генерирует массив минут, доступных в часу `targetHour`, находящихся в промежутке от `startDate` до `endDateISO`, с шагом `step`.
 *
 * Сгенерированные минуты принимают значения, кратные шагу `step`
 * @param startDateISO Дата в формате ISO - левая граница промежутка
 * @param endDateISO Дата в формате ISO - правая граница промежутка
 * @param targetHour Час, для которого генерируются минуты
 * @param step Шаг, с которым генерируются минуты
 * @returns массив минут, находящихся в промежутке от `startDate` до `endDateISO`, с шагом `step`
 * @example
 * ```
 * const startDate = '2024-08-27T00:48'
 * const endDate = '2024-08-27T02:45'
 * const step = 10;
 *
 * getMinutesInHourRange(startDate, endDate, 0, step) // ['50']
 * getMinutesInHourRange(startDate, endDate, 1, step) // ['00', '10', '20', '30', '40', '50']
 * getMinutesInHourRange(startDate, endDate, 2, step) // ['00', '10', '20', '30', '40']
 * ```
 */
export const getMinutesInRange = (
  startDateISO: string,
  endDateISO: string,
  targetHour: number,
  step?: number,
): Array<string> => {
  if (targetHour < 0 || targetHour > 23) {
    throw new Error('targetHour must be between 0 and 23.')
  }

  const startDate = dayjs(startDateISO)
  const endDate = dayjs(endDateISO)

  if (startDate.isAfter(endDate)) {
    throw new Error('startDateISO must be earlier than endDateISO')
  }

  const startHour = startDate.hour()
  const endHour = endDate.hour()

  if (startHour > targetHour || endHour < targetHour) {
    return []
  }

  const startMinute = targetHour === startHour ? startDate.minute() : 0
  const endMinute = targetHour === endHour ? endDate.minute() : 59

  let minutesRange = range(startMinute, endMinute + 1)

  if (step) {
    minutesRange = minutesRange.filter(val => val % step === 0)
  }

  return minutesRange.map(hour => String(hour).padStart(2, '0'))
}
